import { bindable, customElement, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import type {
  PDFDocumentLoadingTask,
  PDFDocumentProxy,
  PDFPageProxy,
  RenderTask,
  TextLayer,
} from 'pdfjs-dist';
import template from './pdf-viewer.html';
import { ConfirmDialog } from './confirm-dialog';
import { DateTimeValueConverter } from '../converters/date-time';
import {
  createPdfDocumentLink,
  createPdfBookmark,
  createPdfMarker,
  deletePdfBookmark,
  deleteAllPdfMarkers,
  deletePdfMarker,
  getPdfDocumentLinks,
  getPdfBookmarksForFile,
  getPdfMarkers,
  getUserSettings,
  listDocuments,
  updatePdfBookmark,
  updatePdfMarkerComment,
} from '../services/api.service';
import type {
  Document,
  PdfBookmark,
  PdfDocumentLink,
  PdfMarker,
  PdfMarkerRectangle,
  Tag,
} from '../services/api.types';
import { measurePdfText, type PdfTextGeometry } from '../services/pdf-text-geometry';
import { PDF_MARKER_HOLD_DELAY, PdfPencilSelection, type PencilSelection } from '../services/pdf-pencil-selection';
import {
  DEFAULT_PDF_HIGHLIGHT_COLORS,
  PDF_HIGHLIGHT_COLORS_SETTING_KEY,
  parsePdfHighlightColors,
} from '../services/pdf-marker-colors';

type PdfJsModule = typeof import('pdfjs-dist');

let nextViewerId = 1;

type PdfPageState = {
  page: PDFPageProxy;
  wrapper: HTMLDivElement;
  surface: HTMLDivElement;
  canvas: HTMLCanvasElement;
  markerLayer: HTMLDivElement;
  selectionLayer: HTMLDivElement;
  bookmarkLayer: HTMLDivElement;
  annotationLayer: HTMLDivElement;
  textGeometry: PdfTextGeometry | null;
  textLayer: HTMLDivElement;
  endOfContent: HTMLDivElement | null;
  renderTask: RenderTask | null;
  textLayerTask: TextLayer | null;
  renderPromise: Promise<void> | null;
  renderKey: string | null;
  textLayerCopyHandler: ((event: ClipboardEvent) => void) | null;
  textLayerMouseDownHandler: (() => void) | null;
};

@customElement({ name: 'pdf-viewer', template, dependencies: [DateTimeValueConverter] })
export class PdfViewer {
  @bindable public src = '';
  @bindable public title = '';
  @bindable public initialPage = 1;
  @bindable public initialRectangles: PdfMarkerRectangle[] = [];
  @bindable public documentId = '';
  @bindable public fileId = '';
  @bindable public linkOpened: ((documentId: string) => void) | null = null;

  public loading = false;
  public error = '';
  public pageCount = 0;
  public currentPage = 1;
  public zoom = 1;
  public highlightColors: string[] = [...DEFAULT_PDF_HIGHLIGHT_COLORS];
  public selectedHighlightColor: string = DEFAULT_PDF_HIGHLIGHT_COLORS[0];
  public colorPickerOpen = false;
  public colorControlElement!: HTMLElement;
  public colorMenuElement!: HTMLElement;
  public colorToggleElement!: HTMLButtonElement;
  public markers: PdfMarker[] = [];
  public markerError = '';
  public commentDialogVisible = false;
  public pendingComment = '';
  public pendingMarkerColor: string = DEFAULT_PDF_HIGHLIGHT_COLORS[0];
  public markerSaving = false;
  public selectedText = '';
  public selectionPageNumber = 0;
  public selectionRectangles: PdfMarkerRectangle[] = [];
  public hasSelection = false;
  public selectedMarker: PdfMarker | null = null;
  public editedComment = '';
  public editedMarkerColor = '';
  public activeMarkerId = '';
  public bookmarks: PdfBookmark[] = [];
  public bookmarkError = '';
  public bookmarkDialogVisible = false;
  public selectedBookmark: PdfBookmark | null = null;
  public pendingBookmarkTitle = '';
  public pendingBookmarkDescription = '';
  public pendingBookmarkTags: Tag[] = [];
  public pendingBookmarkPageNumber = 0;
  public pendingBookmarkSelectedText = '';
  public pendingBookmarkRectangles: PdfMarkerRectangle[] = [];
  public bookmarkSaving = false;
  public documentLinks: PdfDocumentLink[] = [];
  public documentLinkError = '';
  public documentLinkDialogVisible = false;
  public documentLinkSearch = '';
  public documentLinkSearchResults: Document[] = [];
  public documentLinkSearchLoading = false;
  public selectedDocumentLinkTarget: Document | null = null;
  public documentLinkSaving = false;
  public pendingDocumentLinkPageNumber = 0;
  public pendingDocumentLinkSelectedText = '';
  public pendingDocumentLinkRectangles: PdfMarkerRectangle[] = [];
  public readonly pageInputId = `pdf-viewer-page-${nextViewerId++}`;

  public contentElement!: HTMLDivElement;
  public pagesElement!: HTMLDivElement;

  private readonly i18n: I18N = resolve(I18N);

  private attachedToDom = false;
  private loadVersion = 0;
  private loadingTask: PDFDocumentLoadingTask | null = null;
  private pdf: PDFDocumentProxy | null = null;
  private pdfJsModule: PdfJsModule | null = null;
  private pageStates: PdfPageState[] = [];
  private pageObserver: IntersectionObserver | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private renderTimer: number | null = null;
  private markerLoadVersion = 0;
  private bookmarkLoadVersion = 0;
  private documentLinkLoadVersion = 0;
  private documentLinkSearchTimer: ReturnType<typeof setTimeout> | null = null;
  private documentLinkSearchVersion = 0;
  private documentLinkSearchSource = '';
  private pencil: PdfPencilSelection | null = null;
  private isPointerDown = false;
  private lastPointerType = 'mouse';
  private selectionRetained = false;
  private markerHoldTimer: number | null = null;
  private annotationTooltip: HTMLDivElement | null = null;
  private annotationTooltipAnchor: HTMLButtonElement | null = null;
  private bookmarkTooltip: HTMLDivElement | null = null;
  private bookmarkTooltipAnchor: HTMLButtonElement | null = null;
  private markerScrollPosition: { top: number; left: number } | null = null;
  private hoveredDocumentLink: { pageNumber: number; linkId: string } | null = null;

  public get orderedMarkers(): PdfMarker[] {
    return [...this.markers].sort((a, b) =>
      a.pageNumber - b.pageNumber ||
      (a.rectangles[0]?.y ?? 0) - (b.rectangles[0]?.y ?? 0) ||
      (a.rectangles[0]?.x ?? 0) - (b.rectangles[0]?.x ?? 0) ||
      a.id.localeCompare(b.id));
  }

  public get currentMarkerNumber(): number {
    return this.markers.length
      ? Math.max(0, this.orderedMarkers.findIndex(marker => marker.id === this.activeMarkerId)) + 1 : 0;
  }

  public get markerEditDirty(): boolean {
    return Boolean(this.selectedMarker && (this.editedComment !== this.selectedMarker.comment ||
      this.editedMarkerColor !== this.selectedMarker.color));
  }

  public get annotationColors(): string[] {
    return [...new Set([...this.highlightColors, ...(this.selectedMarker ? [this.selectedMarker.color] : [])])];
  }

  public get markerWasUpdated(): boolean {
    const marker = this.selectedMarker;
    return Boolean(marker?.lastUpdatedAt &&
      (Date.parse(marker.lastUpdatedAt) !== Date.parse(marker.createdAt) ||
        marker.lastUpdater !== marker.creator));
  }

  public get bookmarkEditDirty(): boolean {
    const bookmark = this.selectedBookmark;
    return Boolean(bookmark && (
      this.pendingBookmarkTitle.trim() !== bookmark.title ||
      this.pendingBookmarkDescription !== bookmark.description ||
      this.pendingBookmarkTags.map(tag => tag.name).join('\u0000') !==
        bookmark.tags.map(tag => tag.name).join('\u0000')
    ));
  }

  public get bookmarkTitleValid(): boolean {
    return this.pendingBookmarkTitle.trim().length > 0;
  }

  public get bookmarkWasUpdated(): boolean {
    const bookmark = this.selectedBookmark;
    return Boolean(bookmark?.lastUpdatedAt &&
      (Date.parse(bookmark.lastUpdatedAt) !== Date.parse(bookmark.createdAt) ||
        bookmark.lastUpdater !== bookmark.creator));
  }
  private textGesture: {
    pointerId: number;
    x: number;
    y: number;
    startedAt: number;
    moved: boolean;
    hadSelection: boolean;
  } | null = null;
  private selectionJumpWorkaroundNeeded: boolean | null = null;
  private selectionJumpPrevRange: Range | null = null;
  private readonly selectionChangeHandler = (): void => {
    if (this.pencil?.ownsSelection) return;
    if (this.lastPointerType === 'mouse') {
      this.updateTextLayerSelectionClasses(document.getSelection());
    }
    this.updateSelectionFromBrowser();
  };
  private readonly globalPointerDownHandler = (event: PointerEvent): void => {
    this.hideAnnotationTooltip();
    if (this.colorPickerOpen && event.target instanceof Node &&
        !this.colorControlElement.contains(event.target)) this.closeColorPicker();
    this.isPointerDown = true;
    this.lastPointerType = event.pointerType || 'mouse';
    if (event.pointerType === 'mouse') {
      this.updateDocumentLinkHover(event);
    }
  };
  private readonly globalPointerMoveHandler = (event: PointerEvent): void => {
    this.moveTextGesture(event);
    this.updateDocumentLinkHover(event);
  };
  private readonly globalPointerUpHandler = (): void => {
    this.cancelMarkerHold();
    this.isPointerDown = false;
    if (this.lastPointerType === 'mouse') {
      this.resetAllTextLayerSelectionState();
    }
  };
  private readonly globalBlurHandler = (): void => {
    this.hideAnnotationTooltip();
    this.closeColorPicker();
    this.cancelTextGesture();
    this.clearDocumentLinkHover();
    this.isPointerDown = false;
    if (this.lastPointerType === 'mouse') {
      this.resetAllTextLayerSelectionState();
    }
  };
  private readonly globalKeyUpHandler = (): void => {
    if (!this.isPointerDown && this.lastPointerType === 'mouse') {
      this.resetAllTextLayerSelectionState();
    }
  };
  private readonly renderMargin = 800;

  public attached(): void {
    this.attachedToDom = true;
    this.pencil = new PdfPencilSelection(this.contentElement, {
      getPage: (x, y) => {
        if (this.markerSaving || this.commentDialogVisible || this.loading) return null;
        const state = this.pageStateAtPoint(x, y);
        return state?.textGeometry ? {
          pageNumber: state.page.pageNumber,
          surface: state.surface,
          geometry: state.textGeometry,
        } : null;
      },
      onChange: selection => this.applyPencilSelection(selection),
      onTap: (x, y) => this.showMarkerAtPoint(x, y),
      onLongPress: (x, y) => this.deleteHighlightAtPoint(x, y),
    });
    document.addEventListener('selectionchange', this.selectionChangeHandler);
    document.addEventListener('pointerdown', this.globalPointerDownHandler);
    document.addEventListener('pointerup', this.globalPointerUpHandler);
    document.addEventListener('pointermove', this.globalPointerMoveHandler);
    document.addEventListener('keyup', this.globalKeyUpHandler);
    window.addEventListener('blur', this.globalBlurHandler);
    this.resizeObserver = new ResizeObserver(() => this.scheduleRelayout());
    this.resizeObserver.observe(this.contentElement);
    void this.loadHighlightColors();
    void this.loadPdf();
  }

  public detached(): void {
    this.closeColorPicker();
    this.cancelMarkerHold();
    this.attachedToDom = false;
    this.pencil?.destroy();
    this.pencil = null;
    this.loadVersion++;
    this.markerLoadVersion++;
    this.bookmarkLoadVersion++;
    this.documentLinkLoadVersion++;
    this.documentLinkSearchVersion++;
    if (this.documentLinkSearchTimer !== null) {
      clearTimeout(this.documentLinkSearchTimer);
      this.documentLinkSearchTimer = null;
    }
    document.removeEventListener('selectionchange', this.selectionChangeHandler);
    document.removeEventListener('pointerdown', this.globalPointerDownHandler);
    document.removeEventListener('pointerup', this.globalPointerUpHandler);
    document.removeEventListener('pointermove', this.globalPointerMoveHandler);
    document.removeEventListener('keyup', this.globalKeyUpHandler);
    window.removeEventListener('blur', this.globalBlurHandler);
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    this.disposePdf();
  }

  public srcChanged(newValue: string, oldValue: string): void {
    if (this.attachedToDom && newValue !== oldValue) {
      void this.loadPdf();
    }
  }

  public initialPageChanged(newValue: number): void {
    if (this.pageStates.length > 0) {
      this.goToPage(newValue);
    }
  }

  public initialRectanglesChanged(): void {
    if (this.pageStates.length > 0 && this.initialRectangles.length > 0) {
      this.scrollToInitialPosition();
    }
  }

  public documentIdChanged(): void {
    this.refreshMarkersIfReady();
    this.refreshBookmarksIfReady();
    this.refreshDocumentLinksIfReady();
  }

  public fileIdChanged(): void {
    this.refreshMarkersIfReady();
    this.refreshBookmarksIfReady();
    this.refreshDocumentLinksIfReady();
  }

  public previousPage(): void {
    this.goToPage(this.currentPage - 1);
  }

  public nextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  public goToPageFromInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.goToPage(Number(input.value));
  }

  public zoomOut(): void {
    this.setZoom(this.zoom - 0.1);
  }

  public resetZoom(): void {
    this.setZoom(1);
  }

  public zoomIn(): void {
    this.setZoom(this.zoom + 0.1);
  }

  public toggleColorPicker(): void {
    if (this.markerSaving) return;
    if (this.colorPickerOpen) {
      this.closeColorPicker(true);
      return;
    }
    this.retainSelection();
    this.colorPickerOpen = true;
    requestAnimationFrame(() => {
      if (this.colorPickerOpen && this.attachedToDom) {
        const menu = this.colorMenuElement.getBoundingClientRect();
        const viewer = this.contentElement.getBoundingClientRect();
        const shift = Math.max(viewer.left + 8 - menu.left, Math.min(0, viewer.right - 8 - menu.right));
        this.colorMenuElement.style.transform = `translateX(${shift}px)`;
        this.colorMenuElement?.querySelector<HTMLButtonElement>('[aria-checked="true"]')?.focus();
      }
    });
  }

  public async chooseHighlightColor(color: string): Promise<void> {
    if (this.markerSaving || !this.highlightColors.includes(color)) return;
    this.selectedHighlightColor = color;
    this.closeColorPicker(true);
    if (this.hasSelection) await this.quickHighlight(color);
  }

  private closeColorPicker(restoreFocus = false): void {
    this.colorPickerOpen = false;
    if (restoreFocus) this.colorToggleElement?.focus();
  }

  public onColorPickerFocusOut(event: FocusEvent): boolean {
    if (!(event.relatedTarget instanceof Node) ||
        !this.colorControlElement.contains(event.relatedTarget)) this.closeColorPicker();
    return true;
  }

  public onColorPickerKeyDown(event: KeyboardEvent): boolean {
    if (event.key === 'Escape' && this.colorPickerOpen) {
      this.closeColorPicker(true);
      event.stopPropagation();
      return false;
    }
    if (!['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return true;
    if (!this.colorPickerOpen) {
      this.toggleColorPicker();
      return false;
    }
    const buttons = Array.from(this.colorMenuElement.querySelectorAll<HTMLButtonElement>('button'));
    if (!buttons.length) return true;
    const current = buttons.findIndex(button => button === document.activeElement);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? buttons.length - 1 :
      (current + (event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1) + buttons.length) % buttons.length;
    buttons[next].focus();
    return false;
  }

  public async quickHighlight(color: string): Promise<void> {
    this.closeColorPicker();
    if (
      !this.hasSelection ||
      !this.documentId ||
      !this.fileId ||
      this.markerSaving ||
      !this.highlightColors.includes(color)
    ) {
      return;
    }

    this.selectedHighlightColor = color;
    await this.persistMarker(color, '');
  }

  public openBookmarkDialog(): void {
    if (!this.documentId || !this.fileId || this.bookmarkSaving) {
      return;
    }

    this.hideAnnotationTooltip();
    this.selectedBookmark = null;
    this.selectedMarker = null;
    this.pendingBookmarkPageNumber = this.hasSelection
      ? this.selectionPageNumber
      : this.currentPage;
    this.pendingBookmarkSelectedText = this.hasSelection ? this.selectedText : '';
    this.pendingBookmarkRectangles = this.hasSelection
      ? this.selectionRectangles.map(rectangle => ({ ...rectangle }))
      : [];
    this.pendingBookmarkTitle = this.pendingBookmarkSelectedText ||
      this.i18n.tr('documents.form.pdfViewer.bookmarkPageTitle', {
        page: this.pendingBookmarkPageNumber,
      });
    this.pendingBookmarkDescription = '';
    this.pendingBookmarkTags = [];
    this.bookmarkError = '';
    this.selectionRetained = this.hasSelection;
    this.bookmarkDialogVisible = true;
  }

  public openBookmarkDetail(bookmark: PdfBookmark): void {
    if (this.bookmarkSaving) return;
    this.hideAnnotationTooltip();
    this.clearSelectionState(true);
    this.selectedMarker = null;
    this.selectedBookmark = bookmark;
    this.pendingBookmarkTitle = bookmark.title;
    this.pendingBookmarkDescription = bookmark.description;
    this.pendingBookmarkTags = bookmark.tags.map(tag => ({ ...tag }));
    this.pendingBookmarkPageNumber = bookmark.pageNumber;
    this.pendingBookmarkSelectedText = bookmark.selectedText;
    this.pendingBookmarkRectangles = bookmark.rectangles.map(rectangle => ({ ...rectangle }));
    this.bookmarkError = '';
    this.bookmarkDialogVisible = true;
  }

  public cancelBookmarkDialog(): void {
    if (this.bookmarkSaving) return;
    this.bookmarkDialogVisible = false;
    this.selectedBookmark = null;
    this.pendingBookmarkTags = [];
    this.pendingBookmarkTitle = '';
    this.pendingBookmarkDescription = '';
    this.pendingBookmarkSelectedText = '';
    this.pendingBookmarkRectangles = [];
    this.clearSelectionState(true);
  }

  public openDocumentLinkDialog(): void {
    if (!this.documentId || !this.fileId || !this.hasSelection || this.documentLinkSaving) {
      return;
    }

    this.hideAnnotationTooltip();
    this.closeColorPicker();
    this.selectedMarker = null;
    this.selectedBookmark = null;
    this.pendingDocumentLinkPageNumber = this.selectionPageNumber;
    this.pendingDocumentLinkSelectedText = this.selectedText;
    this.pendingDocumentLinkRectangles = this.selectionRectangles.map(rectangle => ({ ...rectangle }));
    this.documentLinkSearch = '';
    this.documentLinkSearchResults = [];
    this.documentLinkSearchLoading = false;
    this.selectedDocumentLinkTarget = null;
    this.documentLinkError = '';
    this.selectionRetained = true;
    this.documentLinkDialogVisible = true;
  }

  public cancelDocumentLinkDialog(): void {
    if (this.documentLinkSaving) return;
    if (this.documentLinkSearchTimer !== null) {
      clearTimeout(this.documentLinkSearchTimer);
      this.documentLinkSearchTimer = null;
    }
    this.documentLinkSearchVersion++;
    this.documentLinkSearchSource = '';
    this.documentLinkDialogVisible = false;
    this.documentLinkSearch = '';
    this.documentLinkSearchResults = [];
    this.documentLinkSearchLoading = false;
    this.selectedDocumentLinkTarget = null;
    this.pendingDocumentLinkSelectedText = '';
    this.pendingDocumentLinkRectangles = [];
    this.documentLinkError = '';
    this.clearSelectionState(true);
  }

  public onDocumentLinkSearchInput(): void {
    this.selectedDocumentLinkTarget = null;
    this.documentLinkError = '';
    if (this.documentLinkSearchTimer !== null) {
      clearTimeout(this.documentLinkSearchTimer);
      this.documentLinkSearchTimer = null;
    }

    const query = this.documentLinkSearch.trim();
    const searchVersion = ++this.documentLinkSearchVersion;
    this.documentLinkSearchSource = query;
    if (!query) {
      this.documentLinkSearchResults = [];
      this.documentLinkSearchLoading = false;
      return;
    }

    this.documentLinkSearchLoading = true;
    this.documentLinkSearchTimer = setTimeout(() => {
      this.documentLinkSearchTimer = null;
      void this.searchDocumentLinkTargets(query, searchVersion);
    }, 250);
  }

  private async searchDocumentLinkTargets(query: string, searchVersion: number): Promise<void> {
    try {
      const response = await listDocuments(
        'createdAt',
        true,
        1,
        8,
        undefined,
        undefined,
        query,
        false,
        undefined,
        false,
        undefined,
        undefined,
        false,
        true,
      );
      if (searchVersion !== this.documentLinkSearchVersion ||
          query !== this.documentLinkSearchSource) {
        return;
      }

      this.documentLinkSearchResults = response.documents
        .filter(document => document.id !== this.documentId);
    } catch (caughtError) {
      if (searchVersion !== this.documentLinkSearchVersion ||
          query !== this.documentLinkSearchSource) {
        return;
      }

      this.documentLinkSearchResults = [];
      this.documentLinkError = caughtError instanceof Error
        ? caughtError.message
        : String(caughtError);
    } finally {
      if (searchVersion === this.documentLinkSearchVersion &&
          query === this.documentLinkSearchSource) {
        this.documentLinkSearchLoading = false;
      }
    }
  }

  public selectDocumentLinkTarget(document: Document): void {
    if (this.documentLinkSearchTimer !== null) {
      clearTimeout(this.documentLinkSearchTimer);
      this.documentLinkSearchTimer = null;
    }
    this.documentLinkSearchVersion++;
    this.documentLinkSearchSource = '';
    this.documentLinkSearchLoading = false;
    this.selectedDocumentLinkTarget = document;
    this.documentLinkSearch = document.title;
    this.documentLinkSearchResults = [];
    this.documentLinkError = '';
  }

  public clearDocumentLinkTarget(): void {
    if (this.documentLinkSearchTimer !== null) {
      clearTimeout(this.documentLinkSearchTimer);
      this.documentLinkSearchTimer = null;
    }
    this.documentLinkSearchVersion++;
    this.documentLinkSearchSource = '';
    this.documentLinkSearchLoading = false;
    this.selectedDocumentLinkTarget = null;
    this.documentLinkSearch = '';
    this.documentLinkSearchResults = [];
    this.documentLinkError = '';
  }

  public async saveDocumentLink(): Promise<void> {
    if (
      this.documentLinkSaving ||
      !this.documentId ||
      !this.fileId ||
      !this.selectedDocumentLinkTarget ||
      !this.pendingDocumentLinkSelectedText
    ) {
      return;
    }

    const version = this.loadVersion;
    const pageNumber = this.pendingDocumentLinkPageNumber;
    const selectedText = this.pendingDocumentLinkSelectedText;
    const rectangles = this.pendingDocumentLinkRectangles.map(rectangle => ({ ...rectangle }));
    this.documentLinkSaving = true;
    this.documentLinkError = '';
    try {
      const link = await createPdfDocumentLink(this.documentId, this.fileId, {
        pageNumber,
        selectedText,
        rectangles,
        targetDocumentId: this.selectedDocumentLinkTarget.id,
      });
      if (version !== this.loadVersion) return;

      this.documentLinks = [...this.documentLinks, link];
      this.documentLinkDialogVisible = false;
      this.documentLinkSearch = '';
      this.documentLinkSearchResults = [];
      this.selectedDocumentLinkTarget = null;
      this.pendingDocumentLinkSelectedText = '';
      this.pendingDocumentLinkRectangles = [];
      this.clearSelectionState(true);
      const state = this.pageStates[link.pageNumber - 1];
      if (state) {
        this.renderMarkerOverlays(state);
        this.renderBookmarkOverlays(state);
      }
    } catch (caughtError) {
      if (version === this.loadVersion) {
        this.documentLinkError = caughtError instanceof Error
          ? caughtError.message
          : String(caughtError);
      }
    } finally {
      if (version === this.loadVersion) this.documentLinkSaving = false;
    }
  }

  public openDocumentLink(link: PdfDocumentLink): void {
    if (!link.targetDocumentId) return;
    if (this.linkOpened) {
      this.linkOpened(link.targetDocumentId);
      return;
    }

    window.location.assign(`/documents?openDocumentId=${encodeURIComponent(link.targetDocumentId)}`);
  }

  public async saveBookmark(): Promise<void> {
    if (this.bookmarkSaving || !this.documentId || !this.fileId) return;
    const title = this.pendingBookmarkTitle.trim();
    if (!title) {
      this.bookmarkError = this.i18n.tr('documents.form.pdfViewer.bookmarkTitleRequired');
      return;
    }

    const version = this.loadVersion;
    const documentId = this.documentId;
    const fileId = this.fileId;
    const selectedBookmark = this.selectedBookmark;
    this.bookmarkSaving = true;
    this.bookmarkError = '';
    try {
      const tagNames = this.pendingBookmarkTags.map(tag => tag.name.trim()).filter(Boolean);
      const saved = selectedBookmark
        ? await updatePdfBookmark(documentId, fileId, selectedBookmark.id, {
          title,
          description: this.pendingBookmarkDescription.trim(),
          tagNames,
        })
        : await createPdfBookmark(documentId, fileId, {
          pageNumber: this.pendingBookmarkPageNumber,
          selectedText: this.pendingBookmarkSelectedText,
          title,
          description: this.pendingBookmarkDescription.trim(),
          rectangles: this.pendingBookmarkRectangles,
          tagNames,
        });
      if (version !== this.loadVersion) return;

      this.bookmarks = selectedBookmark
        ? this.bookmarks.map(bookmark => bookmark.id === saved.id ? saved : bookmark)
        : [...this.bookmarks, saved];
      this.bookmarkDialogVisible = false;
      this.selectedBookmark = null;
      this.pendingBookmarkTags = [];
      if (!selectedBookmark) {
        this.clearSelectionState(true);
      }
      const state = this.pageStates[saved.pageNumber - 1];
      if (state) this.renderBookmarkOverlays(state);
    } catch (caughtError) {
      if (version === this.loadVersion) {
        this.bookmarkError = caughtError instanceof Error ? caughtError.message : String(caughtError);
      }
    } finally {
      if (version === this.loadVersion) this.bookmarkSaving = false;
    }
  }

  public async deleteSelectedBookmark(): Promise<void> {
    const bookmark = this.selectedBookmark;
    if (!bookmark || this.bookmarkSaving || !this.documentId || !this.fileId) return;
    const confirmed = await ConfirmDialog.confirm(
      this.i18n.tr('documents.form.pdfViewer.deleteBookmarkConfirm', { title: bookmark.title }),
      this.i18n.tr('documents.form.pdfViewer.deleteBookmarkConfirmTitle'),
    );
    if (!confirmed) return;

    const version = this.loadVersion;
    this.bookmarkSaving = true;
    this.bookmarkError = '';
    try {
      await deletePdfBookmark(this.documentId, this.fileId, bookmark.id);
      if (version !== this.loadVersion) return;
      this.bookmarks = this.bookmarks.filter(candidate => candidate.id !== bookmark.id);
      this.bookmarkDialogVisible = false;
      this.selectedBookmark = null;
      const state = this.pageStates[bookmark.pageNumber - 1];
      if (state) this.renderBookmarkOverlays(state);
    } catch (caughtError) {
      if (version === this.loadVersion) {
        this.bookmarkError = caughtError instanceof Error ? caughtError.message : String(caughtError);
      }
    } finally {
      if (version === this.loadVersion) this.bookmarkSaving = false;
    }
  }

  public openMarkerCommentDialog(): void {
    if (!this.hasSelection || !this.documentId || !this.fileId) {
      return;
    }

    this.pendingMarkerColor = this.selectedHighlightColor;
    this.pendingComment = '';
    this.markerError = '';
    this.commentDialogVisible = true;
  }

  public selectPendingCommentColor(color: string): void {
    if (this.highlightColors.includes(color)) {
      this.pendingMarkerColor = color;
      this.selectedHighlightColor = color;
    }
  }

  public cancelMarkerComment(): void {
    this.commentDialogVisible = false;
    this.pendingComment = '';
    this.pendingMarkerColor = this.selectedHighlightColor;
    this.clearSelectionState(true);
  }

  public async saveMarker(): Promise<void> {
    if (
      !this.hasSelection ||
      !this.documentId ||
      !this.fileId ||
      this.markerSaving
    ) {
      return;
    }

    const saved = await this.persistMarker(this.pendingMarkerColor, this.pendingComment);
    if (saved) {
      this.commentDialogVisible = false;
      this.pendingComment = '';
      this.pendingMarkerColor = this.selectedHighlightColor;
    }
  }

  public openMarkerDetail(marker: PdfMarker): void {
    this.hideAnnotationTooltip();
    if (this.markerSaving) return;
    this.clearSelectionState(true);
    this.selectedMarker = marker;
    this.editedComment = marker.comment;
    this.editedMarkerColor = marker.color;
    this.setActiveMarker(marker.id);
    this.markerError = '';
  }

  public retainSelection(): boolean {
    this.selectionRetained = this.hasSelection;
    return true;
  }

  public beginTextGesture(event: PointerEvent): boolean {
    this.cancelMarkerHold();
    if (event.target instanceof Element && event.target.closest('button, select')) return true;
    if (event.pointerType === 'pen' || this.pencil?.isActive) return true;
    this.pencil?.clear();
    this.selectionRetained = false;
    const selection = document.getSelection();
    this.textGesture = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      startedAt: event.timeStamp,
      moved: false,
      hadSelection: Boolean(selection && !selection.isCollapsed),
    };
    if (!this.textGesture.hadSelection && event.button === 0) {
      const marker = this.markerAtPoint(event.clientX, event.clientY);
      if (marker && !marker.comment.trim()) {
        this.markerHoldTimer = window.setTimeout(() => {
          this.markerHoldTimer = null;
          if (this.textGesture && !this.textGesture.moved) {
            this.deleteHighlightAtPoint(event.clientX, event.clientY);
          }
        }, PDF_MARKER_HOLD_DELAY);
      }
    }
    return true;
  }

  public moveTextGesture(event: PointerEvent): boolean {
    const gesture = this.textGesture;
    if (gesture?.pointerId === event.pointerId &&
        Math.hypot(event.clientX - gesture.x, event.clientY - gesture.y) > 6) {
      gesture.moved = true;
      this.cancelMarkerHold();
    }
    return true;
  }

  public cancelTextGesture(): boolean {
    this.cancelMarkerHold();
    this.textGesture = null;
    return true;
  }

  public openMarkerAtPoint(event: MouseEvent): boolean {
    if (this.pencil?.ownsSelection) return true;
    const gesture = this.textGesture;
    this.textGesture = null;
    const selection = document.getSelection();
    if (!gesture || gesture.moved || gesture.hadSelection ||
        event.timeStamp - gesture.startedAt > 500 ||
        Math.hypot(event.clientX - gesture.x, event.clientY - gesture.y) > 6 ||
        (selection && !selection.isCollapsed)) {
      return true;
    }

    const documentLink = this.documentLinkAtPoint(event.clientX, event.clientY);
    if (this.lastPointerType === 'mouse' && documentLink) {
      event.preventDefault();
      this.setDocumentLinkHover({
        pageNumber: documentLink.state.page.pageNumber,
        linkId: documentLink.link.id,
      });
      this.openDocumentLink(documentLink.link);
      return true;
    }

    this.showMarkerAtPoint(event.clientX, event.clientY);
    return true;
  }

  private pageStateAtPoint(x: number, y: number): PdfPageState | undefined {
    return this.pageStates.find(candidate => {
      const bounds = candidate.surface.getBoundingClientRect();
      return x >= bounds.left && x <= bounds.right && y >= bounds.top && y <= bounds.bottom;
    });
  }

  private documentLinkAtPoint(
    clientX: number,
    clientY: number,
  ): { state: PdfPageState; link: PdfDocumentLink } | undefined {
    const state = this.pageStateAtPoint(clientX, clientY);
    if (!state) return undefined;

    const bounds = state.surface.getBoundingClientRect();
    if (bounds.width <= 0 || bounds.height <= 0) return undefined;
    const x = (clientX - bounds.left) / bounds.width;
    const y = (clientY - bounds.top) / bounds.height;
    const pageNumber = state.page.pageNumber;
    const link = [...this.documentLinks].reverse().find(candidate =>
      candidate.pageNumber === pageNumber &&
      candidate.rectangles.some(rectangle =>
        x >= rectangle.x && x <= rectangle.x + rectangle.width &&
        y >= rectangle.y && y <= rectangle.y + rectangle.height));
    return link ? { state, link } : undefined;
  }

  private updateDocumentLinkHover(event: PointerEvent): void {
    if (event.pointerType !== 'mouse') {
      this.clearDocumentLinkHover();
      return;
    }

    const documentLink = this.documentLinkAtPoint(event.clientX, event.clientY);
    this.setDocumentLinkHover(documentLink
      ? {
        pageNumber: documentLink.state.page.pageNumber,
        linkId: documentLink.link.id,
      }
      : null);
  }

  private setDocumentLinkHover(
    hoveredDocumentLink: { pageNumber: number; linkId: string } | null,
  ): void {
    if (
      this.hoveredDocumentLink?.pageNumber === hoveredDocumentLink?.pageNumber &&
      this.hoveredDocumentLink?.linkId === hoveredDocumentLink?.linkId
    ) {
      return;
    }

    this.hoveredDocumentLink = hoveredDocumentLink;
    this.refreshDocumentLinkHoverStyles();
  }

  private refreshDocumentLinkHoverStyles(): void {
    const hovered = this.hoveredDocumentLink;
    const hoveredLink = hovered
      ? this.documentLinks.find(link =>
        link.pageNumber === hovered.pageNumber && link.id === hovered.linkId)
      : undefined;

    for (const state of this.pageStates) {
      const pageNumber = state.page.pageNumber;
      for (const highlight of state.markerLayer.querySelectorAll<HTMLElement>(
        '.pdf-viewer__document-link-highlight',
      )) {
        highlight.classList.toggle(
          'pdf-viewer__document-link-highlight--hovered',
          pageNumber === hovered?.pageNumber &&
            highlight.dataset.documentLinkId === hovered?.linkId,
        );
      }

      const surfaceBounds = state.surface.getBoundingClientRect();
      for (const textSpan of state.textLayer.querySelectorAll<HTMLElement>('span')) {
        const textBounds = textSpan.getBoundingClientRect();
        const left = (textBounds.left - surfaceBounds.left) / surfaceBounds.width;
        const top = (textBounds.top - surfaceBounds.top) / surfaceBounds.height;
        const right = (textBounds.right - surfaceBounds.left) / surfaceBounds.width;
        const bottom = (textBounds.bottom - surfaceBounds.top) / surfaceBounds.height;
        const isLinkedText = Boolean(
          hoveredLink &&
          hoveredLink.pageNumber === pageNumber &&
          surfaceBounds.width > 0 &&
          surfaceBounds.height > 0 &&
          right > 0 &&
          bottom > 0 &&
          left < 1 &&
          top < 1 &&
          hoveredLink.rectangles.some(rectangle =>
            right > rectangle.x &&
            left < rectangle.x + rectangle.width &&
            bottom > rectangle.y &&
            top < rectangle.y + rectangle.height),
        );
        textSpan.classList.toggle(
          'pdf-viewer__document-link-text--hovered',
          isLinkedText,
        );
      }
    }
  }

  private clearDocumentLinkHover(): void {
    this.setDocumentLinkHover(null);
  }

  private showMarkerAtPoint(clientX: number, clientY: number): boolean {
    const marker = this.markerAtPoint(clientX, clientY);
    if (marker?.comment.trim()) {
      this.openMarkerDetail(marker);
      return true;
    }
    return false;
  }

  private markerAtPoint(clientX: number, clientY: number): PdfMarker | undefined {
    const state = this.pageStateAtPoint(clientX, clientY);
    if (!state) return undefined;
    const bounds = state.surface.getBoundingClientRect();
    const x = (clientX - bounds.left) / bounds.width;
    const y = (clientY - bounds.top) / bounds.height;
    return [...this.markers].reverse().find(candidate =>
      candidate.pageNumber === Number(state.wrapper.dataset.pageNumber) &&
      candidate.rectangles.some(rectangle =>
        x >= rectangle.x && x <= rectangle.x + rectangle.width &&
        y >= rectangle.y && y <= rectangle.y + rectangle.height));
  }

  private cancelMarkerHold(): void {
    if (this.markerHoldTimer !== null) window.clearTimeout(this.markerHoldTimer);
    this.markerHoldTimer = null;
  }

  public onHighlightContextMenu(event: MouseEvent): boolean {
    if (this.textGesture?.moved) return true;
    return !this.deleteHighlightAtPoint(event.clientX, event.clientY);
  }

  private deleteHighlightAtPoint(x: number, y: number): boolean {
    const marker = this.markerAtPoint(x, y);
    if (!marker || marker.comment.trim() || this.markerSaving) return false;
    this.cancelTextGesture();
    this.clearSelectionState(true);
    void this.confirmDeleteMarker(marker);
    return true;
  }

  private applyPencilSelection(selection: PencilSelection | null): void {
    if (selection) {
      this.selectedText = selection.text;
      this.selectionPageNumber = selection.pageNumber;
      this.selectionRectangles = selection.rectangles;
      this.hasSelection = Boolean(selection.text);
      this.selectedMarker = null;
      this.markerError = '';
    } else {
      this.selectionRetained = false;
      this.selectedText = '';
      this.selectionPageNumber = 0;
      this.selectionRectangles = [];
      this.hasSelection = false;
    }
    for (const state of this.pageStates) this.renderPencilPreview(state);
  }

  public async closeMarkerDetail(): Promise<void> {
    if (this.markerSaving) return;
    if (this.markerEditDirty &&
        !await ConfirmDialog.confirm(
          this.i18n.tr('documents.form.unsavedChanges'),
          this.i18n.tr('documents.form.unsavedChangesTitle'))) return;
    this.selectedMarker = null;
  }

  public async saveMarkerComment(): Promise<void> {
    const marker = this.selectedMarker;
    if (!marker || this.markerSaving || !this.markerEditDirty) return;
    const version = this.loadVersion;
    this.markerSaving = true;
    this.markerError = '';
    try {
      const updated = await updatePdfMarkerComment(
        this.documentId, this.fileId, marker.id, this.editedComment.trim(), this.editedMarkerColor);
      if (version !== this.loadVersion) return;
      this.markers = this.markers.map(candidate => candidate.id === updated.id ? updated : candidate);
      this.selectedMarker = null;
      const state = this.pageStates[updated.pageNumber - 1];
      if (state) this.renderMarkerOverlays(state);
    } catch (caughtError) {
      if (version === this.loadVersion) {
        this.markerError = caughtError instanceof Error ? caughtError.message : String(caughtError);
      }
    } finally {
      if (version === this.loadVersion) this.markerSaving = false;
    }
  }

  public async deleteSelectedMarker(): Promise<void> {
    const marker = this.selectedMarker;
    if (marker) await this.confirmDeleteMarker(marker);
  }

  private async confirmDeleteMarker(marker: PdfMarker): Promise<void> {
    if (this.markerSaving || !this.documentId || !this.fileId) return;
    const version = this.loadVersion;
    const documentId = this.documentId;
    const fileId = this.fileId;
    this.markerSaving = true;
    this.markerError = '';
    try {
      if (!await ConfirmDialog.confirm(
        this.i18n.tr('documents.form.pdfViewer.deleteHighlightConfirm'),
        this.i18n.tr('documents.form.pdfViewer.deleteHighlightConfirmTitle'),
      ) || version !== this.loadVersion) return;
      await deletePdfMarker(documentId, fileId, marker.id);
      if (version !== this.loadVersion) return;
      const index = this.currentMarkerNumber - 1;
      this.markers = this.markers.filter(candidate => candidate.id !== marker.id);
      if (this.activeMarkerId === marker.id) {
        this.setActiveMarker(this.orderedMarkers[Math.min(index, this.markers.length - 1)]?.id ?? '');
      }
      this.selectedMarker = null;
      const state = this.pageStates[marker.pageNumber - 1];
      if (state) {
        this.renderMarkerOverlays(state);
      }
    } catch (caughtError) {
      if (version === this.loadVersion) {
        this.markerError = caughtError instanceof Error ? caughtError.message : String(caughtError);
      }
    } finally {
      if (version === this.loadVersion) this.markerSaving = false;
    }
  }

  public async confirmDeleteAllMarkers(): Promise<void> {
    if (this.markerSaving || !this.documentId || !this.fileId || this.markers.length === 0) {
      return;
    }

    const version = this.loadVersion;
    const documentId = this.documentId;
    const fileId = this.fileId;
    this.markerSaving = true;
    this.markerError = '';
    try {
      if (!await ConfirmDialog.confirm(
        this.i18n.tr('documents.form.pdfViewer.deleteAllHighlightsConfirm', { count: this.markers.length }),
        this.i18n.tr('documents.form.pdfViewer.deleteAllHighlightsConfirmTitle'),
      ) || version !== this.loadVersion) return;
      await deleteAllPdfMarkers(documentId, fileId);
      if (version !== this.loadVersion) return;
      this.markers = [];
      this.activeMarkerId = '';
      this.selectedMarker = null;
      for (const state of this.pageStates) {
        this.renderMarkerOverlays(state);
      }
    } catch (caughtError) {
      if (version === this.loadVersion) {
        this.markerError = caughtError instanceof Error ? caughtError.message : String(caughtError);
      }
    } finally {
      if (version === this.loadVersion) this.markerSaving = false;
    }
  }

  private async persistMarker(color: string, comment: string): Promise<boolean> {
    const version = this.loadVersion;
    const pageNumber = this.selectionPageNumber;
    const selectedText = this.selectedText;
    const rectangles = this.selectionRectangles.map(rectangle => ({ ...rectangle }));
    this.markerSaving = true;
    this.markerError = '';

    try {
      const marker = await createPdfMarker(this.documentId, this.fileId, {
        pageNumber,
        selectedText,
        color,
        comment: comment.trim(),
        rectangles,
      });
      if (version !== this.loadVersion) return false;
      this.markers = [...this.markers, marker];
      this.setActiveMarker(marker.id);
      const state = this.pageStates[pageNumber - 1];
      if (state) {
        this.renderMarkerOverlays(state);
      }
      this.clearSelectionState(true);
      return true;
    } catch (caughtError) {
      if (version !== this.loadVersion) return false;
      this.markerError = caughtError instanceof Error ? caughtError.message : String(caughtError);
      return false;
    } finally {
      if (version === this.loadVersion) this.markerSaving = false;
    }
  }

  public previousMarker(): void {
    if (this.currentMarkerNumber > 1) this.goToMarker(this.currentMarkerNumber - 2);
  }

  public nextMarker(): void {
    if (this.currentMarkerNumber < this.markers.length) this.goToMarker(this.currentMarkerNumber);
  }

  private setActiveMarker(id: string): void {
    if (id === this.activeMarkerId) return;
    this.activeMarkerId = id;
    for (const state of this.pageStates) this.renderMarkerOverlays(state);
  }

  private goToMarker(index: number): void {
    const marker = this.orderedMarkers[index];
    const state = marker && this.pageStates[marker.pageNumber - 1];
    const rectangle = marker?.rectangles[0];
    if (!state || !rectangle) return;
    this.clearSelectionState(true);
    this.setActiveMarker(marker.id);
    this.currentPage = marker.pageNumber;
    void this.renderPage(state, this.loadVersion);
    const content = this.contentElement.getBoundingClientRect();
    const surface = state.surface.getBoundingClientRect();
    this.contentElement.scrollTo({
      top: this.contentElement.scrollTop + surface.top - content.top +
        rectangle.y * surface.height - this.contentElement.clientHeight * 0.35,
      left: this.contentElement.scrollLeft + surface.left - content.left +
        (rectangle.x + rectangle.width / 2) * surface.width - this.contentElement.clientWidth / 2,
      behavior: 'instant',
    });
    this.markerScrollPosition = { top: this.contentElement.scrollTop, left: this.contentElement.scrollLeft };
    this.renderPagesInView(this.loadVersion);
  }

  private scrollToInitialPosition(): void {
    if (!this.pdf || this.pageStates.length === 0 || this.initialRectangles.length === 0) {
      if (this.pageCount > 0 && this.initialPage !== this.currentPage) {
        this.goToPage(this.initialPage, false);
      }
      return;
    }

    const pageNumber = this.clampPage(this.initialPage);
    const state = this.pageStates[pageNumber - 1];
    const rectangle = this.initialRectangles[0];
    if (!state || !rectangle) return;

    this.currentPage = pageNumber;
    void this.renderPage(state, this.loadVersion);
    requestAnimationFrame(() => {
      if (!this.attachedToDom || state.wrapper.parentElement !== this.pagesElement) return;
      const content = this.contentElement.getBoundingClientRect();
      const surface = state.surface.getBoundingClientRect();
      if (surface.width <= 0 || surface.height <= 0) return;
      this.contentElement.scrollTo({
        top: Math.max(0, this.contentElement.scrollTop + surface.top - content.top +
          rectangle.y * surface.height - this.contentElement.clientHeight * 0.28),
        left: Math.max(0, this.contentElement.scrollLeft + surface.left - content.left +
          (rectangle.x + rectangle.width / 2) * surface.width - this.contentElement.clientWidth / 2),
        behavior: 'auto',
      });
      this.renderPagesInView(this.loadVersion);
    });
  }

  public onScroll(): void {
    this.hideAnnotationTooltip();
    this.cancelMarkerHold();
    this.textGesture = null;
    if (!this.contentElement || this.pageStates.length === 0) {
      return;
    }

    const targetPosition = this.contentElement.scrollTop + this.contentElement.clientHeight * 0.35;
    let closestPage = this.currentPage;
    let closestDistance = Number.POSITIVE_INFINITY;

    for (const state of this.pageStates) {
      const pageCenter = state.wrapper.offsetTop + state.wrapper.offsetHeight / 2;
      const distance = Math.abs(pageCenter - targetPosition);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestPage = Number(state.wrapper.dataset.pageNumber);
      }
    }

    if (closestPage !== this.currentPage) {
      this.currentPage = closestPage;
    }

    const locked = this.markerScrollPosition &&
      Math.abs(this.markerScrollPosition.top - this.contentElement.scrollTop) < 1 &&
      Math.abs(this.markerScrollPosition.left - this.contentElement.scrollLeft) < 1;
    if (!locked) {
      this.markerScrollPosition = null;
      const content = this.contentElement.getBoundingClientRect();
      const y = content.top + this.contentElement.clientHeight * 0.35;
      let closest: PdfMarker | undefined;
      let distance = Infinity;
      for (const marker of this.orderedMarkers) {
        const surface = this.pageStates[marker.pageNumber - 1]?.surface.getBoundingClientRect();
        if (!surface || !marker.rectangles[0]) continue;
        const delta = Math.abs(surface.top + marker.rectangles[0].y * surface.height - y);
        if (delta < distance) {
          closest = marker;
          distance = delta;
        }
      }
      if (closest) this.setActiveMarker(closest.id);
    }
    this.renderPagesInView(this.loadVersion);
  }

  private async loadPdf(): Promise<void> {
    this.closeColorPicker();
    this.cancelMarkerHold();
    const version = ++this.loadVersion;
    this.disposePdf();
    this.error = '';
    this.markerError = '';
    this.markers = [];
    this.activeMarkerId = '';
    this.markerScrollPosition = null;
    this.bookmarks = [];
    this.bookmarkError = '';
    this.documentLinks = [];
    this.documentLinkError = '';
    this.documentLinkDialogVisible = false;
    this.documentLinkSearch = '';
    this.documentLinkSearchResults = [];
    this.documentLinkSearchLoading = false;
    this.selectedDocumentLinkTarget = null;
    this.pendingDocumentLinkSelectedText = '';
    this.pendingDocumentLinkRectangles = [];
    this.documentLinkSaving = false;
    this.selectedBookmark = null;
    this.bookmarkDialogVisible = false;
    this.pendingBookmarkTags = [];
    this.bookmarkSaving = false;
    this.selectedMarker = null;
    this.commentDialogVisible = false;
    this.pendingComment = '';
    this.markerSaving = false;
    this.clearSelectionState(true);
    this.pageCount = 0;
    this.currentPage = 1;
    this.loading = Boolean(this.src);
    this.pagesElement?.replaceChildren();

    if (!this.src) {
      this.loading = false;
      return;
    }

    try {
      const pdfjs = await this.loadPdfJs();
      if (version !== this.loadVersion) {
        return;
      }

      const loadingTask = pdfjs.getDocument({
        url: this.src,
        withCredentials: true,
      });
      this.loadingTask = loadingTask;
      const loadedPdf = await loadingTask.promise;

      if (version !== this.loadVersion) {
        await loadingTask.destroy();
        return;
      }

      this.pdf = loadedPdf;
      this.pageCount = loadedPdf.numPages;
      this.currentPage = this.clampPage(this.initialPage);

      for (let pageNumber = 1; pageNumber <= loadedPdf.numPages; pageNumber++) {
        if (version !== this.loadVersion) {
          return;
        }

        const page = await loadedPdf.getPage(pageNumber);
        const wrapper = document.createElement('div');
        wrapper.className = 'pdf-viewer__page';
        wrapper.dataset.pageNumber = String(pageNumber);
        wrapper.setAttribute('aria-label', `${this.title || 'PDF'} - ${pageNumber}`);

        const surface = document.createElement('div');
        surface.className = 'pdf-viewer__surface';

        const canvas = document.createElement('canvas');
        canvas.className = 'pdf-viewer__canvas';
        canvas.setAttribute('aria-label', `${this.title || 'PDF'} - ${pageNumber}`);

        const markerLayer = document.createElement('div');
        markerLayer.className = 'pdf-viewer__marker-layer';
        const selectionLayer = document.createElement('div');
        selectionLayer.className = 'pdf-viewer__selection-layer';
        selectionLayer.setAttribute('aria-hidden', 'true');
        const bookmarkLayer = document.createElement('div');
        bookmarkLayer.className = 'pdf-viewer__bookmark-layer';
        const annotationLayer = document.createElement('div');
        annotationLayer.className = 'pdf-viewer__annotation-layer';

        const textLayer = document.createElement('div');
        textLayer.className = 'pdf-viewer__text-layer';

        surface.append(canvas, markerLayer, selectionLayer, textLayer, bookmarkLayer, annotationLayer);
        wrapper.append(surface);
        this.pagesElement.append(wrapper);

        const textLayerCopyHandler = (event: ClipboardEvent): void => {
          const selection = document.getSelection();
          if (!selection || selection.isCollapsed || !event.clipboardData) {
            return;
          }

          event.clipboardData.setData('text/plain', selection.toString().replaceAll('\0', ''));
          event.preventDefault();
        };
        const textLayerMouseDownHandler = (): void => {
          if (this.lastPointerType === 'mouse') {
            textLayer.classList.add('selecting');
          }
        };
        textLayer.addEventListener('copy', textLayerCopyHandler);
        textLayer.addEventListener('mousedown', textLayerMouseDownHandler);

        const state: PdfPageState = {
          page,
          wrapper,
          surface,
          canvas,
          markerLayer,
          selectionLayer,
          bookmarkLayer,
          annotationLayer,
          textGeometry: null,
          textLayer,
          endOfContent: null,
          renderTask: null,
          textLayerTask: null,
          renderPromise: null,
          renderKey: null,
          textLayerCopyHandler,
          textLayerMouseDownHandler,
        };
        this.pageStates.push(state);
        this.layoutPage(state, true);
      }

      await Promise.all([
        this.loadMarkers(version),
        this.loadBookmarks(version),
        this.loadDocumentLinks(version),
      ]);
      if (version !== this.loadVersion) {
        return;
      }

      this.pageObserver = new IntersectionObserver(
        entries => {
          for (const entry of entries) {
            if (!entry.isIntersecting) {
              continue;
            }

            const pageNumber = Number((entry.target as HTMLElement).dataset.pageNumber);
            const state = this.pageStates[pageNumber - 1];
            if (state) {
              void this.renderPage(state, version);
            }
          }
        },
        {
          root: this.contentElement,
          rootMargin: `${this.renderMargin}px 0px`,
          threshold: 0.01,
        },
      );

      for (const state of this.pageStates) {
        this.pageObserver.observe(state.wrapper);
      }

      this.loading = false;
      this.goToPage(this.currentPage, false);
      this.scrollToInitialPosition();
      this.renderCurrentPage(version);
      this.renderPagesInView(version);
    } catch (caughtError) {
      if (version !== this.loadVersion) {
        return;
      }

      this.error = caughtError instanceof Error ? caughtError.message : String(caughtError);
      this.loading = false;
    }
  }

  private async loadPdfJs(): Promise<PdfJsModule> {
    if (this.pdfJsModule) {
      return this.pdfJsModule;
    }

    const [pdfjs, workerSource] = await Promise.all([
      import('pdfjs-dist'),
      import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
    ]);
    pdfjs.GlobalWorkerOptions.workerSrc = workerSource.default;
    this.pdfJsModule = pdfjs;
    return pdfjs;
  }

  private async loadHighlightColors(): Promise<void> {
    try {
      const settings = await getUserSettings();
      const setting = settings.find(item => item.key === PDF_HIGHLIGHT_COLORS_SETTING_KEY);
      this.highlightColors = parsePdfHighlightColors(setting?.value);
      if (!this.highlightColors.includes(this.selectedHighlightColor)) {
        this.selectedHighlightColor = this.highlightColors[0];
        this.pendingMarkerColor = this.selectedHighlightColor;
      }
    } catch {
      this.highlightColors = [...DEFAULT_PDF_HIGHLIGHT_COLORS];
      this.selectedHighlightColor = this.highlightColors[0];
      this.pendingMarkerColor = this.selectedHighlightColor;
    }
  }

  private refreshMarkersIfReady(): void {
    if (!this.attachedToDom || !this.pdf || !this.documentId || !this.fileId) {
      return;
    }

    void this.loadMarkers(this.loadVersion);
  }

  private refreshBookmarksIfReady(): void {
    if (!this.attachedToDom || !this.pdf || !this.documentId || !this.fileId) {
      return;
    }

    void this.loadBookmarks(this.loadVersion);
  }

  private refreshDocumentLinksIfReady(): void {
    if (!this.attachedToDom || !this.pdf || !this.documentId || !this.fileId) {
      return;
    }

    void this.loadDocumentLinks(this.loadVersion);
  }

  private async loadMarkers(version: number): Promise<void> {
    const markerVersion = ++this.markerLoadVersion;
    if (!this.documentId || !this.fileId) {
      this.markers = [];
      return;
    }

    try {
      const markers = await getPdfMarkers(this.documentId, this.fileId);
      if (
        version !== this.loadVersion ||
        markerVersion !== this.markerLoadVersion
      ) {
        return;
      }

      this.markers = markers;
      this.markerError = '';
      for (const state of this.pageStates) {
        this.renderMarkerOverlays(state);
      }
    } catch (caughtError) {
      if (
        version !== this.loadVersion ||
        markerVersion !== this.markerLoadVersion
      ) {
        return;
      }

      this.markerError = caughtError instanceof Error ? caughtError.message : String(caughtError);
    }
  }

  private async loadBookmarks(version: number): Promise<void> {
    const bookmarkVersion = ++this.bookmarkLoadVersion;
    if (!this.documentId || !this.fileId) {
      this.bookmarks = [];
      return;
    }

    try {
      const bookmarks = await getPdfBookmarksForFile(this.documentId, this.fileId);
      if (
        version !== this.loadVersion ||
        bookmarkVersion !== this.bookmarkLoadVersion
      ) {
        return;
      }

      this.bookmarks = bookmarks;
      this.bookmarkError = '';
      for (const state of this.pageStates) {
        this.renderBookmarkOverlays(state);
      }
    } catch (caughtError) {
      if (
        version !== this.loadVersion ||
        bookmarkVersion !== this.bookmarkLoadVersion
      ) {
        return;
      }

      this.bookmarkError = caughtError instanceof Error ? caughtError.message : String(caughtError);
    }
  }

  private async loadDocumentLinks(version: number): Promise<void> {
    const linkVersion = ++this.documentLinkLoadVersion;
    if (!this.documentId || !this.fileId) {
      this.clearDocumentLinkHover();
      this.documentLinks = [];
      for (const state of this.pageStates) {
        this.renderMarkerOverlays(state);
        this.renderBookmarkOverlays(state);
      }
      return;
    }

    try {
      const links = await getPdfDocumentLinks(this.documentId, this.fileId);
      if (
        version !== this.loadVersion ||
        linkVersion !== this.documentLinkLoadVersion
      ) {
        return;
      }

      this.clearDocumentLinkHover();
      this.documentLinks = links;
      this.documentLinkError = '';
      for (const state of this.pageStates) {
        this.renderMarkerOverlays(state);
        this.renderBookmarkOverlays(state);
      }
    } catch (caughtError) {
      if (
        version !== this.loadVersion ||
        linkVersion !== this.documentLinkLoadVersion
      ) {
        return;
      }

      this.documentLinkError = caughtError instanceof Error
        ? caughtError.message
        : String(caughtError);
    }
  }

  private disposePdf(): void {
    this.hideAnnotationTooltip();
    this.clearDocumentLinkHover();
    if (this.renderTimer !== null) {
      window.clearTimeout(this.renderTimer);
      this.renderTimer = null;
    }

    this.pageObserver?.disconnect();
    this.pageObserver = null;

    for (const state of this.pageStates) {
      state.renderTask?.cancel();
      state.textLayerTask?.cancel();
      state.renderTask = null;
      state.textLayerTask = null;
      state.renderPromise = null;
      if (state.textLayerCopyHandler) {
        state.textLayer.removeEventListener('copy', state.textLayerCopyHandler);
      }
      if (state.textLayerMouseDownHandler) {
        state.textLayer.removeEventListener('mousedown', state.textLayerMouseDownHandler);
      }
    }
    this.clearSelectionState();
    this.textGesture = null;
    this.selectionJumpPrevRange = null;
    this.pageStates = [];

    const loadingTask = this.loadingTask;
    this.loadingTask = null;
    if (loadingTask) {
      void loadingTask.destroy();
    }

    this.pdf = null;
  }

  private goToPage(page: number, smooth = true, preserveScrollLeft?: number): void {
    this.markerScrollPosition = null;
    if (this.pageCount === 0) {
      return;
    }

    const targetPage = this.clampPage(page);
    this.currentPage = targetPage;
    const state = this.pageStates[targetPage - 1];
    if (!state) {
      return;
    }

    void this.renderPage(state, this.loadVersion);
    this.contentElement.scrollTo({
      top: Math.max(0, state.wrapper.offsetTop - 12),
      left: preserveScrollLeft ?? this.contentElement.scrollLeft,
      behavior: smooth ? 'smooth' : 'auto',
    });
  }

  private setZoom(zoom: number): void {
    const nextZoom = Math.min(3, Math.max(0.5, Math.round(zoom * 10) / 10));
    if (nextZoom === this.zoom) {
      return;
    }

    this.zoom = nextZoom;
    this.relayoutPages();
  }

  private scheduleRelayout(): void {
    if (!this.attachedToDom ||
      !(this.contentElement instanceof HTMLElement) ||
      !this.contentElement.isConnected) {
      return;
    }

    this.hideAnnotationTooltip();
    this.closeColorPicker();
    if (!this.pdf || this.renderTimer !== null) {
      return;
    }

    this.renderTimer = window.setTimeout(() => {
      this.renderTimer = null;
      if (!this.attachedToDom ||
        !this.pdf ||
        !(this.contentElement instanceof HTMLElement) ||
        !this.contentElement.isConnected) {
        return;
      }

      // ResizeObserver also fires on attachment and height-only changes.
      // Rebuilding identical text layers would interrupt an active selection.
      const needsLayout = this.pageStates.some(state => {
        const viewport = state.page.getViewport({ scale: this.getPageScale(state.page) });
        return state.surface.style.width !== `${viewport.width}px`;
      });
      if (!needsLayout) {
        this.renderPagesInView(this.loadVersion);
        return;
      }
      this.relayoutPages();
    }, 150);
  }

  private relayoutPages(): void {
    // Keep the finished preview (normalized coordinates), but stop an active
    // stroke before replacing the text geometry underneath it.
    if (this.pencil?.isActive) this.pencil.clear();
    const page = this.currentPage;
    const scrollLeft = this.contentElement.scrollLeft;
    const currentState = this.pageStates[page - 1];
    const pagesToRender = new Set<PdfPageState>();

    for (const state of this.pageStates) {
      if (
        state === currentState
        || state.renderKey !== null
        || state.renderTask !== null
        || state.renderPromise !== null
      ) {
        pagesToRender.add(state);
      }

      state.renderTask?.cancel();
      state.textLayerTask?.cancel();
      state.renderTask = null;
      state.textLayerTask = null;
      state.renderPromise = null;
      state.renderKey = null;
      state.textLayer.replaceChildren();
      state.textLayer.classList.remove('selecting');
      state.endOfContent = null;
      state.textGeometry = null;
      this.layoutPage(state, false);
    }

    requestAnimationFrame(() => {
      this.goToPage(page, false, scrollLeft);
      for (const state of pagesToRender) {
        void this.renderPage(state, this.loadVersion);
      }
      this.renderPagesInView(this.loadVersion);
    });
  }

  private renderCurrentPage(version: number): void {
    const state = this.pageStates[this.currentPage - 1];
    if (state) {
      void this.renderPage(state, version);
    }
  }

  private renderPagesInView(version: number): void {
    if (!this.contentElement) {
      return;
    }

    const viewportTop = this.contentElement.scrollTop - this.renderMargin;
    const viewportBottom = this.contentElement.scrollTop
      + this.contentElement.clientHeight
      + this.renderMargin;

    for (const state of this.pageStates) {
      const pageTop = state.wrapper.offsetTop;
      const pageBottom = pageTop + state.wrapper.offsetHeight;
      if (pageBottom >= viewportTop && pageTop <= viewportBottom) {
        void this.renderPage(state, version);
      }
    }
  }

  private layoutPage(state: PdfPageState, resetCanvas: boolean): void {
    const scale = this.getPageScale(state.page);
    const viewport = state.page.getViewport({ scale });
    state.wrapper.style.width = `${Math.max(this.getAvailablePageWidth(), viewport.width)}px`;
    state.surface.style.width = `${viewport.width}px`;
    state.surface.style.height = `${viewport.height}px`;
    state.canvas.style.width = `${viewport.width}px`;
    state.canvas.style.height = `${viewport.height}px`;

    if (resetCanvas) {
      state.canvas.width = 1;
      state.canvas.height = 1;
    }
    this.renderMarkerOverlays(state);
    this.renderBookmarkOverlays(state);
    this.renderPencilPreview(state);
  }

  private renderPencilPreview(state: PdfPageState): void {
    state.selectionLayer.replaceChildren();
    const selection = this.pencil?.selection;
    if (!selection || selection.pageNumber !== state.page.pageNumber) return;
    for (const rectangle of selection.rectangles) {
      const overlay = document.createElement('div');
      overlay.className = 'pdf-viewer__selection';
      overlay.style.left = `${rectangle.x * 100}%`;
      overlay.style.top = `${rectangle.y * 100}%`;
      overlay.style.width = `${rectangle.width * 100}%`;
      overlay.style.height = `${rectangle.height * 100}%`;
      state.selectionLayer.append(overlay);
    }
  }

  private hideAnnotationTooltip(): void {
    this.annotationTooltipAnchor?.removeAttribute('aria-describedby');
    this.bookmarkTooltipAnchor?.removeAttribute('aria-describedby');
    this.annotationTooltip?.remove();
    this.bookmarkTooltip?.remove();
    this.annotationTooltip = null;
    this.annotationTooltipAnchor = null;
    this.bookmarkTooltip = null;
    this.bookmarkTooltipAnchor = null;
  }

  private showAnnotationTooltip(marker: PdfMarker, bubble: HTMLButtonElement): void {
    if (!this.attachedToDom || this.selectedMarker || this.commentDialogVisible ||
        this.annotationTooltipAnchor === bubble) return;
    this.hideAnnotationTooltip();
    const viewer = this.contentElement.parentElement;
    if (!viewer) return;

    const tooltip = document.createElement('div');
    tooltip.className = 'pdf-viewer__annotation-tooltip';
    tooltip.id = `${this.pageInputId}-annotation-tooltip`;
    tooltip.setAttribute('role', 'tooltip');
    tooltip.textContent = marker.comment;
    tooltip.style.maxWidth = `${Math.max(0, Math.min(320, viewer.clientWidth - 16))}px`;
    tooltip.style.maxHeight = `${Math.max(0, viewer.clientHeight - 16)}px`;
    viewer.append(tooltip);
    const bounds = viewer.getBoundingClientRect();
    const anchor = bubble.getBoundingClientRect();
    const left = anchor.left - bounds.left - tooltip.offsetWidth - 8;
    const top = anchor.top - bounds.top + (anchor.height - tooltip.offsetHeight) / 2;
    tooltip.style.left = `${Math.max(8, Math.min(left, viewer.clientWidth - tooltip.offsetWidth - 8))}px`;
    tooltip.style.top = `${Math.max(8, Math.min(top, viewer.clientHeight - tooltip.offsetHeight - 8))}px`;
    bubble.setAttribute('aria-describedby', tooltip.id);
    this.annotationTooltip = tooltip;
    this.annotationTooltipAnchor = bubble;
  }

  private showBookmarkTooltip(bookmark: PdfBookmark, icon: HTMLButtonElement): void {
    if (!this.attachedToDom || this.bookmarkDialogVisible || this.bookmarkTooltipAnchor === icon) return;
    this.hideAnnotationTooltip();
    const viewer = this.contentElement.parentElement;
    if (!viewer) return;

    const tooltip = document.createElement('div');
    tooltip.className = 'pdf-viewer__bookmark-tooltip';
    tooltip.id = `${this.pageInputId}-bookmark-tooltip`;
    tooltip.setAttribute('role', 'tooltip');
    tooltip.style.maxWidth = `${Math.max(0, Math.min(360, viewer.clientWidth - 16))}px`;
    tooltip.style.maxHeight = `${Math.max(0, viewer.clientHeight - 16)}px`;

    const title = document.createElement('div');
    title.className = 'pdf-viewer__bookmark-tooltip-title';
    title.textContent = bookmark.title;
    tooltip.append(title);

    if (bookmark.tags.length > 0) {
      const tags = document.createElement('div');
      tags.className = 'pdf-viewer__bookmark-tooltip-tags';
      for (const tag of bookmark.tags) {
        const chip = document.createElement('span');
        chip.className = 'tag-chip-preview';
        chip.style.backgroundColor = tag.color;
        chip.textContent = tag.name;
        tags.append(chip);
      }
      tooltip.append(tags);
    }

    viewer.append(tooltip);
    const bounds = viewer.getBoundingClientRect();
    const anchor = icon.getBoundingClientRect();
    const left = anchor.left - bounds.left - tooltip.offsetWidth - 8;
    const top = anchor.top - bounds.top + (anchor.height - tooltip.offsetHeight) / 2;
    tooltip.style.left = `${Math.max(8, Math.min(left, viewer.clientWidth - tooltip.offsetWidth - 8))}px`;
    tooltip.style.top = `${Math.max(8, Math.min(top, viewer.clientHeight - tooltip.offsetHeight - 8))}px`;
    icon.setAttribute('aria-describedby', tooltip.id);
    this.bookmarkTooltip = tooltip;
    this.bookmarkTooltipAnchor = icon;
  }

  private renderMarkerOverlays(state: PdfPageState): void {
    if ((this.annotationTooltipAnchor && state.annotationLayer.contains(this.annotationTooltipAnchor)) ||
        (this.bookmarkTooltipAnchor && state.bookmarkLayer.contains(this.bookmarkTooltipAnchor))) {
      this.hideAnnotationTooltip();
    }
    state.markerLayer.replaceChildren();
    state.annotationLayer.replaceChildren();
    const pageNumber = Number(state.wrapper.dataset.pageNumber);
    for (const link of this.documentLinks) {
      if (link.pageNumber !== pageNumber) {
        continue;
      }

      for (const rectangle of link.rectangles) {
        const highlight = document.createElement('div');
        highlight.className = 'pdf-viewer__document-link-highlight';
        highlight.dataset.documentLinkId = link.id;
        highlight.classList.toggle(
          'pdf-viewer__document-link-highlight--hovered',
          this.hoveredDocumentLink?.pageNumber === pageNumber &&
            this.hoveredDocumentLink?.linkId === link.id,
        );
        highlight.style.left = `${rectangle.x * 100}%`;
        highlight.style.top = `${rectangle.y * 100}%`;
        highlight.style.width = `${rectangle.width * 100}%`;
        highlight.style.height = `${rectangle.height * 100}%`;
        state.markerLayer.append(highlight);
      }
    }

    const occupiedRows = new Map<number, number>();
    for (const marker of this.markers) {
      if (marker.pageNumber !== pageNumber) {
        continue;
      }

      for (const rectangle of marker.rectangles) {
        const overlay = document.createElement('div');
        overlay.className = 'pdf-viewer__marker';
        overlay.classList.toggle('pdf-viewer__marker--current', marker.id === this.activeMarkerId);
        overlay.style.left = `${rectangle.x * 100}%`;
        overlay.style.top = `${rectangle.y * 100}%`;
        overlay.style.width = `${rectangle.width * 100}%`;
        overlay.style.height = `${rectangle.height * 100}%`;
        overlay.style.backgroundColor = marker.color;
        state.markerLayer.append(overlay);
      }
      const first = marker.rectangles[0];
      if (marker.comment.trim() && first) {
        const row = Math.max(0, Math.min(
          Math.floor(first.y * state.surface.clientHeight / 28),
          Math.floor(state.surface.clientHeight / 28) - 1));
        const column = occupiedRows.get(row) ?? 0;
        occupiedRows.set(row, column + 1);
        const bubble = document.createElement('button');
        bubble.type = 'button';
        bubble.className = 'pdf-viewer__annotation-bubble';
        bubble.style.top = `${row * 28}px`;
        bubble.style.right = `${2 + column * 28}px`;
        bubble.setAttribute('aria-label', `${this.i18n.tr('documents.form.pdfViewer.annotation')}: ${marker.comment}`);
        bubble.addEventListener('pointerenter', event => {
          if (event.pointerType !== 'touch' && event.buttons === 0) {
            this.showAnnotationTooltip(marker, bubble);
          }
        });
        bubble.addEventListener('pointerleave', () => {
          if (this.annotationTooltipAnchor === bubble) this.hideAnnotationTooltip();
        });
        bubble.addEventListener('focus', () => {
          if (bubble.matches(':focus-visible')) this.showAnnotationTooltip(marker, bubble);
        });
        bubble.addEventListener('blur', () => {
          if (this.annotationTooltipAnchor === bubble) this.hideAnnotationTooltip();
        });
        bubble.addEventListener('keydown', event => {
          if (event.key === 'Escape' && this.annotationTooltipAnchor === bubble) {
            this.hideAnnotationTooltip();
            event.stopPropagation();
          }
        });
        const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        icon.setAttribute('viewBox', '0 0 24 24');
        icon.setAttribute('width', '22');
        icon.setAttribute('height', '22');
        icon.setAttribute('fill', 'none');
        icon.setAttribute('stroke', 'currentColor');
        icon.setAttribute('stroke-width', '1.8');
        icon.setAttribute('stroke-linejoin', 'round');
        icon.setAttribute('aria-hidden', 'true');
        const outline = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        outline.setAttribute('d', 'M6 4h12a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3h-7l-6 3v-3a3 3 0 0 1-2-3V7a3 3 0 0 1 3-3Z');
        icon.append(outline);
        bubble.append(icon);
        bubble.addEventListener('click', event => {
          event.stopPropagation();
          this.openMarkerDetail(marker);
        });
        state.annotationLayer.append(bubble);
      }
    }
  }

  private renderBookmarkOverlays(state: PdfPageState): void {
    if (this.bookmarkTooltipAnchor && state.bookmarkLayer.contains(this.bookmarkTooltipAnchor)) {
      this.hideAnnotationTooltip();
    }
    state.bookmarkLayer.replaceChildren();
    const pageNumber = Number(state.wrapper.dataset.pageNumber);
    const surfaceHeight = state.surface.clientHeight
      || Number.parseFloat(state.surface.style.height)
      || 0;
    const occupiedRows = new Map<number, number>();
    for (const marker of this.markers) {
      if (marker.pageNumber !== pageNumber || !marker.comment.trim() || !marker.rectangles[0]) {
        continue;
      }
      const row = Math.max(0, Math.min(
        Math.floor(marker.rectangles[0].y * surfaceHeight / 28),
        Math.max(0, Math.floor(surfaceHeight / 28) - 1)));
      occupiedRows.set(row, (occupiedRows.get(row) ?? 0) + 1);
    }

    for (const bookmark of this.bookmarks) {
      if (bookmark.pageNumber !== pageNumber) {
        continue;
      }

      const first = bookmark.rectangles[0];
      const icon = document.createElement('button');
      icon.type = 'button';
      icon.className = `pdf-viewer__bookmark-icon${first ? '' : ' pdf-viewer__bookmark-icon--page'}`;
      icon.setAttribute('aria-label', `${this.i18n.tr('documents.form.pdfViewer.bookmark')}: ${bookmark.title}`);
      if (first) {
        const row = Math.max(0, Math.min(
          Math.floor(first.y * surfaceHeight / 28),
          Math.max(0, Math.floor(surfaceHeight / 28) - 1)));
        const column = occupiedRows.get(row) ?? 0;
        occupiedRows.set(row, column + 1);
        icon.style.top = `${row * 28}px`;
        icon.style.right = `${2 + column * 28}px`;
      } else {
        icon.style.top = '8px';
        icon.style.right = '8px';
      }
      const glyph = document.createElement('i');
      glyph.className = 'fa-solid fa-bookmark';
      glyph.setAttribute('aria-hidden', 'true');
      icon.append(glyph);
      icon.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'touch' && event.buttons === 0) {
          this.showBookmarkTooltip(bookmark, icon);
        }
      });
      icon.addEventListener('pointerleave', () => {
        if (this.bookmarkTooltipAnchor === icon) this.hideAnnotationTooltip();
      });
      icon.addEventListener('focus', () => {
        if (icon.matches(':focus-visible')) this.showBookmarkTooltip(bookmark, icon);
      });
      icon.addEventListener('blur', () => {
        if (this.bookmarkTooltipAnchor === icon) this.hideAnnotationTooltip();
      });
      icon.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.bookmarkTooltipAnchor === icon) {
          this.hideAnnotationTooltip();
          event.stopPropagation();
        }
      });
      icon.addEventListener('pointerdown', event => event.stopPropagation());
      icon.addEventListener('click', event => {
        event.stopPropagation();
        this.openBookmarkDetail(bookmark);
      });
      state.bookmarkLayer.append(icon);
    }

    for (const link of this.documentLinks) {
      if (link.pageNumber !== pageNumber) {
        continue;
      }

      const first = link.rectangles[0];
      const icon = document.createElement('button');
      icon.type = 'button';
      icon.className = `pdf-viewer__document-link-icon${first ? '' : ' pdf-viewer__document-link-icon--page'}`;
      icon.setAttribute(
        'aria-label',
        `${this.i18n.tr('documents.form.pdfViewer.documentLink')}: ${link.targetDocumentTitle}`,
      );
      if (first) {
        const minimumY = Math.min(...link.rectangles.map(rectangle => rectangle.y));
        const maximumY = Math.max(...link.rectangles.map(
          rectangle => rectangle.y + rectangle.height,
        ));
        const centerY = (minimumY + maximumY) / 2;
        const row = Math.max(0, Math.min(
          Math.floor(centerY * surfaceHeight / 28),
          Math.max(0, Math.floor(surfaceHeight / 28) - 1)));
        const column = occupiedRows.get(row) ?? 0;
        occupiedRows.set(row, column + 1);
        icon.style.top = `${Math.max(
          0,
          Math.min(centerY * surfaceHeight - 14, Math.max(0, surfaceHeight - 28)),
        )}px`;
        icon.style.right = `${2 + column * 28}px`;
      } else {
        icon.style.top = '8px';
        icon.style.right = '8px';
      }

      const glyph = document.createElement('i');
      glyph.className = 'fa-solid fa-link';
      glyph.setAttribute('aria-hidden', 'true');
      icon.append(glyph);
      icon.addEventListener('pointerdown', event => event.stopPropagation());
      icon.addEventListener('click', event => {
        event.stopPropagation();
        this.openDocumentLink(link);
      });
      state.bookmarkLayer.append(icon);
    }
  }

  private async renderPage(state: PdfPageState, version: number): Promise<void> {
    const renderKey = this.getRenderKey(state.page);
    if (
      version !== this.loadVersion
      || state.renderPromise
      || state.renderKey === renderKey
    ) {
      return;
    }

    const renderPromise = this.renderPageContents(state, version, renderKey);
    state.renderPromise = renderPromise;

    try {
      await renderPromise;
    } finally {
      if (state.renderPromise === renderPromise) {
        state.renderPromise = null;
      }
    }
  }

  private async renderPageContents(
    state: PdfPageState,
    version: number,
    renderKey: string,
  ): Promise<void> {
    const scale = this.getPageScale(state.page);
    const viewport = state.page.getViewport({ scale });
    const outputScale = window.devicePixelRatio || 1;
    const pdfjs = this.pdfJsModule;
    if (!pdfjs) {
      this.error = 'PDF.js is not loaded.';
      return;
    }

    state.surface.style.width = `${viewport.width}px`;
    state.surface.style.height = `${viewport.height}px`;
    state.canvas.width = Math.max(1, Math.floor(viewport.width * outputScale));
    state.canvas.height = Math.max(1, Math.floor(viewport.height * outputScale));
    state.canvas.style.width = `${viewport.width}px`;
    state.canvas.style.height = `${viewport.height}px`;
    state.textLayer.style.setProperty('--total-scale-factor', String(viewport.scale));
    state.textLayer.replaceChildren();
    state.textLayer.classList.remove('selecting');
    state.endOfContent = null;
    state.textGeometry = null;

    const context = state.canvas.getContext('2d');
    if (!context) {
      this.error = 'Unable to create a canvas context.';
      return;
    }

    const transform = outputScale === 1
      ? undefined
      : [outputScale, 0, 0, outputScale, 0, 0];
    let renderTask: RenderTask | null = null;
    let textLayerTask: TextLayer | null = null;

    try {
      textLayerTask = new pdfjs.TextLayer({
        textContentSource: state.page.streamTextContent({
          includeMarkedContent: true,
          disableNormalization: true,
        }),
        container: state.textLayer,
        viewport,
      });
      state.textLayerTask = textLayerTask;
      const textLayerPromise = textLayerTask.render();

      renderTask = state.page.render({
        canvas: state.canvas,
        viewport,
        transform,
      });
      state.renderTask = renderTask;

      const results = await Promise.allSettled([renderTask.promise, textLayerPromise]);
      const rejected = results.find(
        (result): result is PromiseRejectedResult => result.status === 'rejected',
      );
      if (rejected) {
        throw rejected.reason;
      }

      if (version === this.loadVersion && state.textLayerTask === textLayerTask) {
        state.renderKey = renderKey;
        state.textGeometry = measurePdfText(textLayerTask.textDivs, state.surface);
        const endOfContent = document.createElement('div');
        endOfContent.className = 'endOfContent';
        state.textLayer.append(endOfContent);
        state.endOfContent = endOfContent;
        this.refreshDocumentLinkHoverStyles();
      }
    } catch (caughtError) {
      const errorName = caughtError && typeof caughtError === 'object' && 'name' in caughtError
        ? String(caughtError.name)
        : '';
      if (
        version === this.loadVersion
        && errorName !== 'RenderingCancelledException'
        && errorName !== 'AbortException'
        && errorName !== 'AbortError'
      ) {
        this.error = caughtError instanceof Error ? caughtError.message : String(caughtError);
      }
    } finally {
      if (renderTask && state.renderTask === renderTask) {
        state.renderTask = null;
      }
      if (textLayerTask && state.textLayerTask === textLayerTask) {
        state.textLayerTask = null;
      }
    }
  }

  private getPageScale(page: PDFPageProxy): number {
    const baseViewport = page.getViewport({ scale: 1 });
    const availableWidth = this.getAvailablePageWidth();
    const fitScale = Math.min(1, availableWidth / baseViewport.width);
    return fitScale * this.zoom;
  }

  private getAvailablePageWidth(): number {
    const contentElement = this.contentElement;
    if (!(contentElement instanceof HTMLElement) || !contentElement.isConnected) {
      return 160;
    }

    const styles = getComputedStyle(contentElement);
    const horizontalPadding = Number.parseFloat(styles.paddingLeft)
      + Number.parseFloat(styles.paddingRight);
    return Math.max(160, contentElement.clientWidth - horizontalPadding);
  }

  private getRenderKey(page: PDFPageProxy): string {
    const width = Math.round(this.contentElement.clientWidth);
    const devicePixelRatio = window.devicePixelRatio || 1;
    return `${width}:${this.zoom}:${devicePixelRatio}:${page.pageNumber}`;
  }

  private clampPage(page: number): number {
    if (!Number.isFinite(page)) {
      return 1;
    }

    return Math.min(this.pageCount, Math.max(1, Math.round(page)));
  }

  private updateSelectionFromBrowser(): void {
    if (this.pencil?.ownsSelection || this.commentDialogVisible || this.selectionRetained) {
      return;
    }
    const selection = document.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      this.clearSelectionState();
      return;
    }

    const range = selection.getRangeAt(0);
    const startPage = this.pageElementFromNode(range.startContainer);
    const endPage = this.pageElementFromNode(range.endContainer);
    if (
      !startPage ||
      !endPage ||
      startPage !== endPage ||
      !this.pagesElement.contains(startPage)
    ) {
      this.clearSelectionState();
      return;
    }

    const state = this.pageStates.find(candidate => candidate.wrapper === startPage);
    if (!state) {
      return;
    }

    const selectedText = selection.toString().replaceAll('\0', '').trim();
    const rectangles = this.getNormalizedSelectionRectangles(range, state);
    if (!selectedText || rectangles.length === 0) {
      this.clearSelectionState();
      return;
    }

    this.selectedText = selectedText;
    this.selectionPageNumber = Number(state.wrapper.dataset.pageNumber);
    this.selectionRectangles = rectangles;
    this.hasSelection = true;
  }

  private clearSelectionState(removeBrowserSelection = false): void {
    this.pencil?.clear();
    this.selectionRetained = false;
    this.selectedText = '';
    this.selectionPageNumber = 0;
    this.selectionRectangles = [];
    this.hasSelection = false;
    if (removeBrowserSelection) {
      document.getSelection()?.removeAllRanges();
    }
  }

  private pageElementFromNode(node: Node): HTMLDivElement | null {
    const element = node.nodeType === Node.ELEMENT_NODE
      ? node as Element
      : node.parentElement;
    return element?.closest<HTMLDivElement>('.pdf-viewer__page') ?? null;
  }

  private getNormalizedSelectionRectangles(
    range: Range,
    state: PdfPageState,
  ): PdfMarkerRectangle[] {
    const surfaceBounds = state.surface.getBoundingClientRect();
    if (surfaceBounds.width <= 0 || surfaceBounds.height <= 0) {
      return [];
    }

    const rectangles: PdfMarkerRectangle[] = [];
    for (const clientRect of Array.from(range.getClientRects())) {
      const left = Math.max(clientRect.left, surfaceBounds.left);
      const top = Math.max(clientRect.top, surfaceBounds.top);
      const right = Math.min(clientRect.right, surfaceBounds.right);
      const bottom = Math.min(clientRect.bottom, surfaceBounds.bottom);
      if (right <= left || bottom <= top) {
        continue;
      }

      rectangles.push({
        x: Math.max(0, Math.min(1, (left - surfaceBounds.left) / surfaceBounds.width)),
        y: Math.max(0, Math.min(1, (top - surfaceBounds.top) / surfaceBounds.height)),
        width: Math.max(0, Math.min(1, (right - left) / surfaceBounds.width)),
        height: Math.max(0, Math.min(1, (bottom - top) / surfaceBounds.height)),
      });
    }
    return rectangles;
  }

  private resetAllTextLayerSelectionState(): void {
    for (const state of this.pageStates) {
      this.resetTextLayerSelectionState(state);
    }
  }

  private resetTextLayerSelectionState(state: PdfPageState): void {
    if (state.endOfContent && state.endOfContent.parentElement !== state.textLayer) {
      state.textLayer.append(state.endOfContent);
    }
    if (state.endOfContent) {
      state.endOfContent.style.width = '';
      state.endOfContent.style.height = '';
    }
    state.textLayer.classList.remove('selecting');
  }

  private updateTextLayerSelectionClasses(selection: Selection | null): void {
    if (!selection || selection.rangeCount === 0) {
      this.resetAllTextLayerSelectionState();
      this.selectionJumpPrevRange = null;
      return;
    }

    const activeStates = new Set<PdfPageState>();
    for (let i = 0; i < selection.rangeCount; i++) {
      const range = selection.getRangeAt(i);
      for (const state of this.pageStates) {
        if (!activeStates.has(state) && range.intersectsNode(state.textLayer)) {
          activeStates.add(state);
        }
      }
    }

    for (const state of this.pageStates) {
      if (activeStates.has(state)) {
        state.textLayer.classList.add('selecting');
      } else {
        this.resetTextLayerSelectionState(state);
      }
    }

    if (activeStates.size > 0) {
      this.applySelectionJumpWorkaround(selection);
    } else {
      this.selectionJumpPrevRange = null;
    }
  }

  /**
   * Some browsers (notably Safari) expand a selection all the way to a text
   * layer's trailing empty space when the pointer drags past the last glyph
   * on a line. Repositioning the `.endOfContent` sentinel next to the active
   * selection boundary limits how far the selection can "jump" in that case.
   * Firefox and modern Chromium already handle this correctly, so the
   * workaround is skipped there.
   */
  private isSelectionJumpWorkaroundNeeded(): boolean {
    if (this.selectionJumpWorkaroundNeeded !== null) {
      return this.selectionJumpWorkaroundNeeded;
    }

    const sampleTextLayer = this.pageStates[0]?.textLayer;
    const isFirefoxLike = sampleTextLayer
      ? getComputedStyle(sampleTextLayer).getPropertyValue('-moz-user-select') === 'none'
      : false;

    if (isFirefoxLike) {
      this.selectionJumpWorkaroundNeeded = false;
      return false;
    }

    const navigatorWithUserAgentData = navigator as Navigator & {
      userAgentData?: { brands: { brand: string; version: string }[] };
    };
    const chromiumVersion = navigatorWithUserAgentData.userAgentData
      ? navigatorWithUserAgentData.userAgentData.brands.find(brand => brand.brand === 'Chromium')?.version
      : /\bChrome\/(\d+)\b/.exec(navigator.userAgent)?.[1];
    const isModernChromium = chromiumVersion !== undefined && Number.parseInt(chromiumVersion, 10) >= 148;

    this.selectionJumpWorkaroundNeeded = !isModernChromium;
    return this.selectionJumpWorkaroundNeeded;
  }

  private applySelectionJumpWorkaround(selection: Selection): void {
    if (!this.isSelectionJumpWorkaroundNeeded()) {
      return;
    }

    const range = selection.getRangeAt(0);
    const previousRange = this.selectionJumpPrevRange;
    const modifyStart = Boolean(previousRange) && (
      range.compareBoundaryPoints(Range.END_TO_END, previousRange!) === 0 ||
      range.compareBoundaryPoints(Range.START_TO_END, previousRange!) === 0
    );

    let anchor: Node | null = modifyStart ? range.startContainer : range.endContainer;
    if (anchor && anchor.nodeType === Node.TEXT_NODE) {
      anchor = anchor.parentNode;
    }
    if (anchor instanceof Element && anchor.classList.contains('highlight')) {
      anchor = anchor.parentNode;
    }
    if (!modifyStart && range.endOffset === 0 && anchor instanceof Element) {
      let candidate: Element = anchor;
      while (candidate.previousElementSibling === null && candidate.parentElement) {
        candidate = candidate.parentElement;
      }
      if (candidate.previousElementSibling) {
        anchor = candidate.previousElementSibling;
      }
    }

    const anchorElement = anchor instanceof Element ? anchor : anchor?.parentElement ?? null;
    const parentTextLayer = anchorElement?.closest<HTMLDivElement>('.pdf-viewer__text-layer') ?? null;
    const state = parentTextLayer
      ? this.pageStates.find(candidate => candidate.textLayer === parentTextLayer)
      : undefined;

    if (state?.endOfContent && anchorElement?.parentElement) {
      state.endOfContent.style.width = state.textLayer.style.width;
      state.endOfContent.style.height = state.textLayer.style.height;
      state.endOfContent.style.userSelect = 'text';
      anchorElement.parentElement.insertBefore(
        state.endOfContent,
        modifyStart ? anchorElement : anchorElement.nextSibling,
      );
    }

    this.selectionJumpPrevRange = range.cloneRange();
  }
}
